import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Filter, Activity, ListChecks, Sparkles, Repeat, Gift, X, Check, Clock, Bell, Plus, Mail, ArrowRight, ChevronRight } from "lucide-react";
import { LeadCtaButtons } from "../components/LeadModals";

const PUBLIC_DEMO = "/book/ac9c3982-f96d-4fa3-91e3-cba7234c6e88";

/**
 * Public, no-auth-required interactive showcase. Deep-linkable via hash:
 *   /demo#filtri, /demo#health, /demo#attesa, /demo#autofill, /demo#autobook, /demo#combo
 *
 * All data is in-component mock — no API calls. Works identically in preview
 * and production (www.prenotoo.it).
 */
export default function DemoTour() {
  const SECTIONS = [
    { id: "filtri", label: "Filtri agenda", icon: Filter },
    { id: "health", label: "Health proposte", icon: Activity },
    { id: "attesa", label: "Lista d'attesa", icon: ListChecks },
    { id: "autofill", label: "Riempimento automatico", icon: Sparkles },
    { id: "autobook", label: "Auto-prenotazione", icon: Repeat },
    { id: "combo", label: "Combo & Fedeltà", icon: Gift },
  ];

  // Scroll to hash anchor on load (with offset for sticky header)
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, []);

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen" data-testid="demo-tour">
      {/* Sticky tour nav */}
      <div className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-zinc-100 dark:bg-zinc-900/85 dark:border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 dark:text-zinc-400 dark:hover:text-zinc-100" data-testid="demo-back">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700 hidden md:block" />
          <div className="flex items-center gap-1.5 overflow-x-auto md:overflow-visible -mx-1 px-1">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900 px-2.5 py-1 rounded-full hover:bg-zinc-100 whitespace-nowrap inline-flex items-center gap-1 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800"
                data-testid={`demo-nav-${s.id}`}
              >
                <s.icon className="h-3 w-3" />
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-12">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-3 dark:text-blue-400">Tour interattivo</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium dark:text-zinc-100">
          Tutto Prenotoo, <em className="not-italic text-blue-600 dark:text-blue-400">senza account</em>.
        </h1>
        <p className="text-sm md:text-base text-zinc-600 mt-4 max-w-xl dark:text-zinc-400">
          Esplora le funzionalità più potenti del prodotto. Ogni demo è interattiva con dati mock —
          nessuna registrazione, nessuna prenotazione vera viene creata.
        </p>
      </section>

      <FilterDemo />
      <HealthDemo />
      <WaitlistDemo />
      <AutoFillDemo />
      <AutoBookDemo />
      <ComboDemo />

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="rounded-[2rem] bg-zinc-900 text-white p-10 md:p-14 dark:bg-zinc-100 dark:text-zinc-900">
          <h3 className="font-display text-3xl md:text-4xl tracking-tight font-medium">Convinto? Parliamone.</h3>
          <p className="mt-3 text-zinc-300 max-w-lg dark:text-zinc-700">
            Attivazione su invito: prenota una demo o richiedi l'attivazione. Configurazione assistita, ti rispondiamo entro 24h.
          </p>
          <div className="mt-6">
            <LeadCtaButtons variant="dark" />
          </div>
          <div className="mt-4">
            <a href={PUBLIC_DEMO} className="text-sm text-zinc-300 hover:text-white inline-flex items-center gap-1 dark:text-zinc-600 dark:hover:text-zinc-900" data-testid="demo-final-public">
              Vedi una pagina pubblica reale <ArrowRight className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// Common section wrapper -----------------------------------------------------
function Section({ id, eyebrow, title, desc, children, alt = false }) {
  return (
    <section
      id={id}
      className={`scroll-mt-20 py-12 md:py-20 ${alt ? "bg-zinc-50 dark:bg-zinc-800/40" : ""}`}
      data-testid={`section-${id}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-[minmax(0,420px)_1fr] gap-8 lg:gap-12 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-3 dark:text-blue-400">{eyebrow}</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium dark:text-zinc-100">{title}</h2>
            <p className="text-sm text-zinc-600 mt-3 dark:text-zinc-400">{desc}</p>
          </div>
          <div>{children}</div>
        </div>
      </div>
    </section>
  );
}

// 1. FILTRI AGENDA -----------------------------------------------------------
function FilterDemo() {
  const ALL = [
    { id: "1", time: "08:30", who: "Giulia · Taglio", staff: "sara", staffName: "Sara", color: "bg-emerald-500", status: "confirmed" },
    { id: "2", time: "09:30", who: "Lucia · Manicure", staff: "marta", staffName: "Marta", color: "bg-rose-500", status: "cancelled" },
    { id: "3", time: "10:00", who: "Anna · Pedicure", staff: "sara", staffName: "Sara", color: "bg-emerald-500", status: "confirmed" },
    { id: "4", time: "11:00", who: "Marta · Massaggio", staff: "marta", staffName: "Marta", color: "bg-rose-500", status: "no_show" },
    { id: "5", time: "12:00", who: "Elena · Pulizia viso", staff: "sara", staffName: "Sara", color: "bg-emerald-500", status: "completed" },
    { id: "6", time: "14:00", who: "Roberta · Manicure", staff: "marta", staffName: "Marta", color: "bg-rose-500", status: "confirmed" },
    { id: "7", time: "15:30", who: "Beatrice · Taglio", staff: "sara", staffName: "Sara", color: "bg-emerald-500", status: "confirmed" },
  ];
  const [hideCancelled, setHideCancelled] = useState(true);
  const [hideNoShow, setHideNoShow] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [staff, setStaff] = useState(new Set());
  const counts = { cancelled: ALL.filter(b => b.status === "cancelled").length, no_show: ALL.filter(b => b.status === "no_show").length, completed: ALL.filter(b => b.status === "completed").length };
  const visible = useMemo(() => ALL.filter(b => {
    if (hideCancelled && b.status === "cancelled") return false;
    if (hideNoShow && b.status === "no_show") return false;
    if (hideCompleted && b.status === "completed") return false;
    if (staff.size > 0 && !staff.has(b.staff)) return false;
    return true;
  }), [hideCancelled, hideNoShow, hideCompleted, staff]);
  const activeCount = (hideCancelled ? 1 : 0) + (hideNoShow ? 1 : 0) + (hideCompleted ? 1 : 0) + (staff.size > 0 ? 1 : 0);
  return (
    <Section id="filtri" eyebrow="Filtri Agenda" title="Mostra solo quello che ti interessa." desc="Combinazioni illimitate: nascondi cancellati, no-show, completati, filtra per operatore. Tutto in 2 click.">
      <div className="rounded-3xl bg-white border border-zinc-100 p-5 md:p-6 dark:bg-zinc-950 dark:border-zinc-800">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="font-display text-lg dark:text-zinc-100">Agenda · Lun 19 mag</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <FilterPill label="Cancellati" active={hideCancelled} count={counts.cancelled} onClick={() => setHideCancelled(v => !v)} testid="d-fil-cancelled" />
            <FilterPill label="No-show" active={hideNoShow} count={counts.no_show} onClick={() => setHideNoShow(v => !v)} testid="d-fil-noshow" />
            <FilterPill label="Completati" active={hideCompleted} count={counts.completed} onClick={() => setHideCompleted(v => !v)} testid="d-fil-completed" />
            <FilterPill label="Sara" dot="#10B981" active={staff.has("sara")} onClick={() => { const n = new Set(staff); n.has("sara") ? n.delete("sara") : n.add("sara"); setStaff(n); }} testid="d-fil-sara" />
            <FilterPill label="Marta" dot="#F43F5E" active={staff.has("marta")} onClick={() => { const n = new Set(staff); n.has("marta") ? n.delete("marta") : n.add("marta"); setStaff(n); }} testid="d-fil-marta" />
          </div>
        </div>
        {activeCount > 0 && (
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mb-2">{visible.length} di {ALL.length} appuntamenti visibili</div>
        )}
        <div className="space-y-1.5">
          {visible.map(b => (
            <div key={b.id} className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-900 rounded-lg px-3 py-2 ring-1 ring-zinc-100 dark:ring-zinc-800">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 tabular-nums w-12">{b.time}</span>
              <span className={`h-2 w-2 rounded-full ${b.color}`} />
              <span className="text-xs text-zinc-700 dark:text-zinc-300 truncate flex-1">{b.who}</span>
              <span className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{b.status === "confirmed" ? "" : b.status === "cancelled" ? "annullato" : b.status === "no_show" ? "no-show" : "completato"}</span>
            </div>
          ))}
          {visible.length === 0 && <div className="text-sm text-zinc-400 text-center py-6 dark:text-zinc-500">Nessun appuntamento con questi filtri</div>}
        </div>
      </div>
    </Section>
  );
}

function FilterPill({ label, active, count, dot, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 px-3 rounded-full text-[11px] font-medium border inline-flex items-center gap-1.5 transition ${
        active
          ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
          : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"
      }`}
      data-testid={testid}
    >
      {dot && <span className="h-2 w-2 rounded-full" style={{ background: dot }} />}
      {label}
      {count > 0 && <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums ${active ? "bg-white/20 dark:bg-zinc-900/15" : "bg-zinc-100 dark:bg-zinc-800"}`}>{count}</span>}
    </button>
  );
}

// 2. HEALTH PROPOSTE ---------------------------------------------------------
function HealthDemo() {
  const data6 = [{m:"Dic",a:8,d:3},{m:"Gen",a:12,d:4},{m:"Feb",a:10,d:6},{m:"Mar",a:14,d:3},{m:"Apr",a:16,d:5},{m:"Mag",a:11,d:2}];
  const data3 = data6.slice(-3);
  const [win, setWin] = useState(6);
  const data = win === 6 ? data6 : data3;
  const total = { a: data.reduce((s, x) => s + x.a, 0), d: data.reduce((s, x) => s + x.d, 0) };
  const rate = Math.round(100 * total.a / (total.a + total.d));
  const max = Math.max(...data.map(m => m.a + m.d));
  const reasons = [
    { label: "Vacanza / fuori città", count: 7, pct: 44 },
    { label: "Preferisce un altro orario", count: 5, pct: 31 },
    { label: "Imprevisto / impegni", count: 3, pct: 19 },
  ];
  return (
    <Section id="health" eyebrow="Health proposte" title="Sai perché i clienti rifiutano. Davvero." desc="Tasso di accettazione, top motivi di rifiuto raggruppati per categoria, andamento mensile. Per capire dove ottimizzare." alt>
      <div className="rounded-3xl bg-white border border-zinc-100 p-5 md:p-6 dark:bg-zinc-950 dark:border-zinc-800">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 dark:text-zinc-400">Tasso accettazione</div>
            <div className="font-display text-4xl text-emerald-600 dark:text-emerald-400 tabular-nums">{rate}%</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">{total.a} su {total.a + total.d} risposte</div>
          </div>
          <div className="flex gap-1">
            {[3, 6].map(n => (
              <button key={n} type="button" onClick={() => setWin(n)} className={`h-8 px-3 text-xs rounded-full font-medium border ${win === n ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100" : "bg-white text-zinc-600 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700"}`} data-testid={`d-health-win-${n}`}>{n} mesi</button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500 dark:text-zinc-400 mb-2">Top motivi rifiuto</div>
            <ul className="space-y-2">
              {reasons.map(r => (
                <li key={r.label} className="rounded-xl bg-zinc-50 px-3 py-2 dark:bg-zinc-800">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium dark:text-zinc-100 truncate">{r.label}</span>
                    <span className="text-[11px] font-semibold tabular-nums text-zinc-700 dark:text-zinc-300">{r.count} ({r.pct}%)</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
                    <div className="h-full bg-rose-500 dark:bg-rose-400" style={{ width: `${r.pct}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500 dark:text-zinc-400 mb-2">Andamento {win} mesi</div>
            <div className="rounded-xl bg-zinc-50 dark:bg-zinc-800 px-3 py-3">
              <div className="flex items-end justify-between gap-1.5 h-24">
                {data.map(m => {
                  const tot = m.a + m.d;
                  const totH = Math.max(8, Math.round(tot / max * 88));
                  const aH = Math.round(m.a / tot * totH);
                  return (
                    <div key={m.m} className="flex-1 flex flex-col items-center justify-end">
                      <div className="w-full max-w-[28px] mx-auto rounded-md overflow-hidden flex flex-col-reverse" style={{ height: totH }}>
                        <div className="bg-emerald-500 dark:bg-emerald-400" style={{ height: aH }} />
                        <div className="bg-rose-500 dark:bg-rose-400" style={{ height: totH - aH }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1.5 text-[10px] font-semibold text-zinc-500 dark:text-zinc-400">
                {data.map(m => <span key={m.m} className="flex-1 text-center">{m.m}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// 3. WAITLIST ----------------------------------------------------------------
function WaitlistDemo() {
  const [stage, setStage] = useState(0); // 0 idle, 1 cancelled, 2 notified, 3 booked
  const STAGES = [
    { name: "Sara Bianchi", days: 4, slot: "Ven 15 mag · 16:30", svc: "Manicure" },
  ];
  return (
    <Section id="attesa" eyebrow="Lista d'attesa" title="Una cancellazione = una notifica." desc="Quando uno slot si libera, il sistema avvisa il primo cliente in lista d'attesa che ci entra. In automatico, in pochi secondi.">
      <div className="rounded-3xl bg-white border border-zinc-100 p-5 md:p-6 dark:bg-zinc-950 dark:border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-semibold dark:text-zinc-100">Studio Belle · Lista d'attesa</div>
          <button
            type="button"
            onClick={() => { setStage(1); setTimeout(() => setStage(2), 800); setTimeout(() => setStage(3), 1800); }}
            disabled={stage > 0 && stage < 3}
            className="h-8 px-3 rounded-full text-xs font-medium bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-950/50 dark:text-rose-300 dark:hover:bg-rose-950/70 disabled:opacity-50"
            data-testid="d-waitlist-trigger"
          >
            {stage >= 3 ? "Riavvia demo" : stage > 0 ? "In corso…" : "Simula cancellazione"}
          </button>
          {stage >= 3 && <button type="button" onClick={() => setStage(0)} className="hidden">_</button>}
        </div>
        <div className="space-y-2">
          {stage >= 1 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2.5 flex items-center gap-3 dark:bg-rose-950/30 dark:border-rose-900/50" data-testid="d-waitlist-cancelled">
              <X className="h-5 w-5 text-rose-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-rose-900 dark:text-rose-200">Lucia ha annullato</div>
                <div className="text-[10px] text-rose-700 dark:text-rose-300/80">{STAGES[0].slot} · {STAGES[0].svc}</div>
              </div>
            </motion.div>
          )}
          {stage >= 2 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="d-waitlist-notified">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-600 dark:text-violet-400 inline-flex items-center gap-1">
                <Bell className={`h-3 w-3 ${stage === 2 ? "animate-pulse" : ""}`} /> Notifica push inviata a Sara Bianchi
              </span>
            </motion.div>
          )}
          {stage >= 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 flex items-center gap-3 dark:bg-emerald-950/30 dark:border-emerald-900/50" data-testid="d-waitlist-booked">
              <Check className="h-5 w-5 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-emerald-900 dark:text-emerald-200">{STAGES[0].name} ha accettato</div>
                <div className="text-[10px] text-emerald-700 dark:text-emerald-300/80">Era in attesa da {STAGES[0].days} giorni · slot riempito automaticamente</div>
              </div>
            </motion.div>
          )}
          {stage === 0 && (
            <div className="rounded-xl bg-zinc-50 px-4 py-8 text-center text-xs text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              Premi "Simula cancellazione" per vedere il flusso in azione
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

// 4. AUTO-FILL ---------------------------------------------------------------
function AutoFillDemo() {
  const slots = [
    { time: "Mar 14:00", svc: "Manicure", candidates: [
      { name: "Maria Rossi", reason: "Ricorrente · ogni 28g · scaduta", score: "alta" },
      { name: "Anna Bianchi", reason: "No-show ratio 0%", score: "alta" },
      { name: "Lucia Verdi", reason: "Ha fatto questo servizio 3 volte", score: "media" },
    ]},
    { time: "Gio 11:00", svc: "Pedicure", candidates: [
      { name: "Beatrice Conti", reason: "Ricorrente · ogni 21g", score: "alta" },
      { name: "Elena Marchi", reason: "Cliente fedele", score: "media" },
    ]},
  ];
  const tone = (s) => ({alta:"bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",media:"bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"}[s]);
  return (
    <Section id="autofill" eyebrow="Riempimento automatico" title="Slot vuoto domani? Ti dico chi chiamare." desc="Il sistema analizza lo storico e ti suggerisce i clienti più probabili da contattare per ogni buco in agenda — ordinati per probabilità di accettazione." alt>
      <div className="space-y-4">
        {slots.map(slot => (
          <div key={slot.time} className="rounded-3xl bg-white border border-zinc-100 p-5 dark:bg-zinc-950 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 dark:text-zinc-400">Slot vuoto</div>
                <div className="font-medium text-base dark:text-zinc-100 inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-violet-500" /> {slot.time} · {slot.svc}</div>
              </div>
            </div>
            <ul className="space-y-1.5">
              {slot.candidates.map((c, i) => (
                <li key={i} className="rounded-xl bg-zinc-50 dark:bg-zinc-900 px-3 py-2 ring-1 ring-zinc-100 dark:ring-zinc-800 flex items-center gap-3">
                  <span className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold dark:text-zinc-100 truncate">{c.name}</div>
                    <div className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate">{c.reason}</div>
                  </div>
                  <button type="button" className="h-7 px-2.5 rounded-full text-[10px] font-semibold bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center gap-1" tabIndex={-1}>
                    <Mail className="h-3 w-3" /> Contatta
                  </button>
                  <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${tone(c.score)}`}>{c.score}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

// 5. AUTO-BOOK ---------------------------------------------------------------
function AutoBookDemo() {
  return (
    <Section id="autobook" eyebrow="Auto-prenotazione" title="Clienti abituali? Si prenotano da soli." desc="Il sistema rileva pattern (es. ogni 7 giorni Lun 18:00) e crea l'appuntamento in automatico. Tu attivi/disattivi per ogni cliente.">
      <div className="rounded-3xl bg-white border border-zinc-100 p-5 md:p-6 dark:bg-zinc-950 dark:border-zinc-800">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-display text-lg dark:text-zinc-100">Marta Bianchi</div>
            <div className="text-[11px] text-zinc-500 dark:text-zinc-400">Pulizia viso · €40 · 12 visite negli ultimi 3 mesi</div>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded-full dark:text-amber-300 dark:bg-amber-950/50">
            <Sparkles className="h-2.5 w-2.5" /> Auto-book ON
          </span>
        </div>
        <div className="rounded-xl bg-zinc-50 dark:bg-zinc-900 p-3 mb-3">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500 dark:text-zinc-400 mb-2">Pattern rilevato</div>
          <div className="grid grid-cols-7 gap-1 text-[10px] font-medium">
            {["L","M","M","G","V","S","D"].map((d, i) => (
              <div key={i} className={`h-9 rounded text-center pt-2 ${i === 0 ? "bg-violet-600 text-white" : "bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400"}`}>{d}</div>
            ))}
          </div>
          <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-2">Ogni Lun · 18:00 · cadenza media 7g</div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500 dark:text-zinc-400 mb-2">Prossime auto-prenotazioni</div>
        <div className="space-y-1.5">
          {["Lun 19 mag · 18:00", "Lun 26 mag · 18:00", "Lun 2 giu · 18:00"].map((d, i) => (
            <div key={i} className="rounded-xl bg-violet-50 border border-violet-200 px-3 py-2 dark:bg-violet-950/30 dark:border-violet-900/50 flex items-center gap-2">
              <Plus className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
              <span className="text-xs text-violet-900 dark:text-violet-200 font-medium">{d}</span>
              <span className="text-[10px] text-violet-700/80 dark:text-violet-300/80 ml-auto">€40</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// 6. COMBO + LOYALTY ---------------------------------------------------------
function ComboDemo() {
  const SVC = [
    { id: "m", name: "Manicure", t: 30, p: 30, c: "bg-blue-500" },
    { id: "p", name: "Pedicure", t: 45, p: 35, c: "bg-violet-500" },
    { id: "v", name: "Pulizia viso", t: 45, p: 40, c: "bg-emerald-500" },
    { id: "t", name: "Trattamento mani", t: 20, p: 20, c: "bg-amber-500" },
  ];
  const [selected, setSelected] = useState(["m", "p"]);
  const total = SVC.filter(s => selected.includes(s.id)).reduce((sum, s) => ({ t: sum.t + s.t, p: sum.p + s.p }), { t: 0, p: 0 });
  return (
    <Section id="combo" eyebrow="Combo & Fedeltà" title="Premia chi torna. Vendi servizi insieme." desc="Programma fedeltà automatico (10 visite = sconto) e prenotazioni combo: il cliente sceglie più servizi consecutivi, il sistema trova lo slot in cui entrano tutti." alt>
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Loyalty */}
        <div className="rounded-3xl bg-white border border-zinc-100 p-5 dark:bg-zinc-950 dark:border-zinc-800">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-emerald-600 dark:text-emerald-400 mb-3">🎁 Programma fedeltà</div>
          <div className="font-display text-lg dark:text-zinc-100">Giulia Verdi</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 mb-3">8 / 10 visite</div>
          <div className="h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden mb-3">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" style={{ width: "80%" }} />
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900/50 dark:text-emerald-200">
            🎉 Mancano <strong>2 visite</strong>: poi <strong>Sconto 10%</strong>
          </div>
        </div>
        {/* Combo selector */}
        <div className="rounded-3xl bg-white border border-zinc-100 p-5 dark:bg-zinc-950 dark:border-zinc-800">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 dark:text-blue-400 mb-3">+ Combo prenotazione</div>
          <div className="space-y-1.5 mb-3">
            {SVC.map(s => {
              const on = selected.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelected(on ? selected.filter(x => x !== s.id) : [...selected, s.id])}
                  className={`w-full text-left rounded-xl px-3 py-2 border transition flex items-center gap-2.5 ${on ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900/50" : "border-zinc-200 hover:border-zinc-400 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
                  data-testid={`d-combo-${s.id}`}
                >
                  <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-blue-600 border-blue-600" : "border-zinc-300 dark:border-zinc-600"}`}>
                    {on && <Check className="h-3 w-3 text-white" />}
                  </span>
                  <span className={`h-1.5 w-1.5 rounded-full ${s.c}`} />
                  <span className="text-xs font-medium dark:text-zinc-100 truncate flex-1">{s.name}</span>
                  <span className="text-[11px] text-zinc-400 dark:text-zinc-500 tabular-nums whitespace-nowrap">{s.t} min</span>
                  <span className="text-[11px] text-zinc-700 font-semibold dark:text-zinc-300 tabular-nums">€{s.p}</span>
                </button>
              );
            })}
          </div>
          <div className="rounded-xl bg-zinc-50 dark:bg-zinc-800 px-3 py-2.5 flex items-center justify-between text-xs">
            <span className="text-zinc-500 dark:text-zinc-400">Totale {total.t > 0 ? `${Math.floor(total.t / 60)}h ${total.t % 60}min` : "—"}</span>
            <span className="font-bold text-blue-600 dark:text-blue-400 tabular-nums">€{total.p}</span>
          </div>
          <div className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-2">Il sistema cerca uno slot dove entrano tutti i servizi consecutivamente.</div>
        </div>
      </div>
    </Section>
  );
}
